import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Download, FileSpreadsheet, History, Pencil, Plus, Trash2, Upload } from "lucide-react";
import { useEffect, useState } from "react";
import { api } from "../api";
import { applyWorksheetStyle, styleWorkbook } from "../excel";
import {
  Button,
  CatalogLabel,
  ConfirmDialog,
  EmptyState,
  FormField,
  Modal,
  Pagination,
  Panel,
  SearchBox,
} from "../components/ui";
import { PageHeader } from "../components/shell";
import { formatNumber } from "../numbers";
import type { Catalog, CatalogType, PageMeta } from "../types";

const catalogTabs: Array<{ id: CatalogType; label: string; description: string }> = [
  { id: "material", label: "Vật tư", description: "Nguyên liệu, phụ gia hoặc bao bì được đưa vào từng chi tiết định mức." },
  { id: "product", label: "Thành phẩm", description: "Quy cách thành phẩm dùng để lập định mức theo từng khách hàng và hợp đồng." },
  { id: "customer", label: "Khách hàng", description: "Khách hàng nhận thành phẩm; kết hợp với hợp đồng để xác định đúng định mức." },
  { id: "unit", label: "Đơn vị tính", description: "Đơn vị đo của vật tư và khối lượng, ví dụ kg, gói hoặc cái." },
  { id: "warehouse", label: "Kho", description: "Nơi lưu vật tư; dùng khi đối chiếu tồn kho và tính nhu cầu vật tư." },
];

type CatalogResponse = { items: Catalog[]; meta: PageMeta };
type MaterialHistoryEntry = {
  id: string;
  kind: "bom" | "calculation" | "inventory_use" | "inventory_transfer_out" | "inventory_transfer_in";
  occurredAt: string;
  title: string;
  detail: string;
  quantity: string;
  unitName: string;
  status: string;
};
type MaterialHistoryResponse = { items: MaterialHistoryEntry[]; meta: PageMeta };
type CatalogImportRow = {
  rowNo: number;
  code: string;
  name: string;
  unitCode: string;
  defaultUnitId?: string;
  supplier: string;
  includesSupplier: boolean;
  action: "create" | "update";
  existing?: Catalog;
  errors: string[];
};
type ImportCheck = {
  fileName: string;
  sheetName: string;
  rows: CatalogImportRow[];
  messages: string[];
};

const emptyImportCheck = (): ImportCheck | null => null;

function catalogLabel(type: CatalogType): string {
  return catalogTabs.find((tab) => tab.id === type)?.label ?? "danh mục";
}
function catalogDescription(type: CatalogType): string {
  return catalogTabs.find((tab) => tab.id === type)?.description ?? "Dữ liệu dùng trong hệ thống quản lý định mức.";
}

function normalizedText(value: unknown): string {
  return String(value ?? "").trim();
}

function normalizedHeader(value: unknown): string {
  return normalizedText(value)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/đ/g, "d")
    .toLocaleLowerCase("vi")
    .replace(/[^a-z0-9]/g, "");
}

function codeKey(value: string): string {
  return value.trim().toLocaleLowerCase("vi");
}

function materialSupplier(item: Catalog | null | undefined): string {
  const supplier = item?.attributes.supplier;
  return typeof supplier === "string" ? supplier.trim() : "";
}

function materialUnit(item: Catalog | null | undefined): string {
  const unit = item?.defaultUnit;
  return unit ? `${unit.name}${unit.code ? ` (${unit.code})` : ""}` : "";
}

function attributesWithSupplier(attributes: Record<string, unknown>, supplier: string): Record<string, unknown> {
  const next = { ...attributes };
  const trimmedSupplier = supplier.trim();
  if (trimmedSupplier) next.supplier = trimmedSupplier;
  else delete next.supplier;
  return next;
}

export function CatalogsPage() {
  const [type, setType] = useState<CatalogType>("material");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [edit, setEdit] = useState<Catalog | null | "new">(null);
  const [importOpen, setImportOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  const [historyMaterial, setHistoryMaterial] = useState<Catalog | null>(null);

  useEffect(() => setPage(1), [type, search]);
  const query = useQuery({
    queryKey: ["catalogs", type, search, page],
    queryFn: () =>
      api<CatalogResponse>(
        `/catalogs/${type}?page=${page}&pageSize=25&q=${encodeURIComponent(search)}`,
      ),
  });

  async function downloadTemplate() {
    setDownloadingTemplate(true);
    try {
      const XLSX = await import("xlsx-js-style");
      const headers = type === "material" ? ["Mã", "Tên", "Mã ĐVT", "Nhà cung cấp"] : ["Mã", "Tên"];
      const sheet = XLSX.utils.aoa_to_sheet([
        headers,
      ]);
      applyWorksheetStyle(XLSX, sheet, { headerRows: [0], dataStartsAt: 1, columnWidths: type === "material" ? [22, 38, 16, 32] : [22, 38] });
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, sheet, catalogLabel(type));
      styleWorkbook(XLSX, workbook, `Mẫu nhập ${catalogLabel(type)}`);
      XLSX.writeFile(workbook, `mau-${type}.xlsx`);
    } finally {
      setDownloadingTemplate(false);
    }
  }

  async function exportCatalogs() {
    setExporting(true);
    try {
      const catalogs = await loadAllCatalogs(type);
      const XLSX = await import("xlsx-js-style");
      const sheet = XLSX.utils.json_to_sheet(
        catalogs.map((item) => ({
          "Mã": item.code,
          "Tên": item.name,
          ...(type === "material" ? { "Mã ĐVT": item.defaultUnit?.code ?? "", "Nhà cung cấp": materialSupplier(item) } : {}),
          "Trạng thái": item.isActive ? "Đang dùng" : "Ngừng dùng",
        })),
      );
      applyWorksheetStyle(XLSX, sheet, { headerRows: [0], dataStartsAt: 1, columnWidths: type === "material" ? [22, 38, 16, 32, 16] : [22, 38, 16] });
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, sheet, catalogLabel(type));
      styleWorkbook(XLSX, workbook, `Danh mục ${catalogLabel(type)}`);
      XLSX.writeFile(workbook, `${type}-danh-muc.xlsx`);
    } finally {
      setExporting(false);
    }
  }

  return (
    <div className="page">
      <PageHeader
        title="Danh mục"
        actions={
          <>
            <Button
              variant="outline"
              disabled={downloadingTemplate}
              onClick={downloadTemplate}
            >
              <Download size={16} /> Tải mẫu Excel
            </Button>
            <Button variant="outline" disabled={exporting} onClick={exportCatalogs}>
              <Download size={16} /> Xuất Excel
            </Button>
            <Button variant="outline" onClick={() => setImportOpen(true)}>
              <Upload size={16} /> Nhập Excel
            </Button>
            <Button onClick={() => setEdit("new")}>
              <Plus size={17} /> Thêm {catalogLabel(type).toLocaleLowerCase("vi")}
            </Button>
          </>
        }
      />
      <Panel>
        <div className="table-toolbar">
          <div className="catalog-tabs" aria-label="Loại danh mục">
            {catalogTabs.map((tab) => (
              <button
                className={tab.id === type ? "selected" : ""}
                key={tab.id}
                onClick={() => setType(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>
          <SearchBox
            value={search}
            onChange={setSearch}
            placeholder={type === "material" ? "Tìm theo mã, tên hoặc nhà cung cấp..." : "Tìm theo mã hoặc tên..."}
          />
        </div>
        <div className="catalog-guidance">
          <strong>{catalogLabel(type)}</strong>
          <span>{catalogDescription(type)}</span>
        </div>
        {query.isLoading ? (
          <div className="loading-space">Đang tải danh mục…</div>
        ) : !query.data?.items.length ? (
          <EmptyState
            title={`Chưa có ${catalogLabel(type).toLocaleLowerCase("vi")}`}
            detail={`${catalogDescription(type)} Bạn có thể thêm từng dòng hoặc nhập từ tệp Excel.`}
            action={
              <Button onClick={() => setEdit("new")}>
                Thêm {catalogLabel(type).toLocaleLowerCase("vi")}
              </Button>
            }
          />
        ) : (
          <>
            <div className="table-scroll">
              <table>
                <thead>
                  <tr>
                    <th>Danh mục</th>
                    <th>Trạng thái</th>
                    <th>Cập nhật</th>
                    <th aria-label="Thao tác" />
                  </tr>
                </thead>
                <tbody>
                  {query.data.items.map((item) => (
                    <tr key={item.id}>
                      <td>
                        <CatalogLabel code={item.code} name={item.name} />
                        {type === "material" && <small className={materialUnit(item) ? "catalog-unit" : "catalog-unit missing"}>{materialUnit(item) ? `Đơn vị tính: ${materialUnit(item)}` : "Chưa chọn đơn vị tính"}</small>}
                        {type === "material" && materialSupplier(item) && <small className="catalog-supplier">Nhà cung cấp: {materialSupplier(item)}</small>}
                        {item.isProvisional && <span className="provisional">Tạm</span>}
                      </td>
                      <td>
                        <span className={item.isActive ? "status active" : "status superseded"}>
                          {item.isActive ? "Đang dùng" : "Ngừng dùng"}
                        </span>
                      </td>
                      <td>{formatDate(item.updatedAt)}</td>
                      <td className="row-actions">
                        {type === "material" && <button onClick={() => setHistoryMaterial(item)} title="Lịch sử sử dụng" aria-label={`Lịch sử sử dụng ${item.name}`}>
                          <History size={17} />
                        </button>}
                        <button onClick={() => setEdit(item)} title="Sửa" aria-label={`Sửa ${item.name}`}>
                          <Pencil size={17} />
                        </button>
                        <DeleteButton type={type} id={item.id} name={item.name} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination meta={query.data.meta} onPage={setPage} />
          </>
        )}
      </Panel>
      <CatalogModal type={type} item={edit} onClose={() => setEdit(null)} />
      <CatalogImportModal
        type={type}
        open={importOpen}
        onClose={() => setImportOpen(false)}
      />
      <MaterialHistoryModal material={historyMaterial} onClose={() => setHistoryMaterial(null)} />
    </div>
  );
}

const historyKindLabels: Record<MaterialHistoryEntry["kind"], string> = {
  bom: "Định mức BOM",
  calculation: "Phiếu tính",
  inventory_use: "Đã trừ tồn",
  inventory_transfer_out: "Chuyển kho",
  inventory_transfer_in: "Nhận kho",
};

function historyStatus(status: string) {
  return ({ draft: "Bản nháp", in_review: "Chờ duyệt", active: "Đang áp dụng", superseded: "Hết hiệu lực" } as Record<string, string>)[status] ?? status;
}

function MaterialHistoryModal({ material, onClose }: { material: Catalog | null; onClose(): void }) {
  const [page, setPage] = useState(1);
  useEffect(() => setPage(1), [material?.id]);
  const history = useQuery({
    queryKey: ["material-history", material?.id, page],
    queryFn: () => api<MaterialHistoryResponse>(`/materials/${material?.id}/history?page=${page}&pageSize=20`),
    enabled: Boolean(material),
  });
  return <Modal open={Boolean(material)} title="Lịch sử sử dụng vật tư" onClose={onClose}>
    <div className="modal-form material-history-modal">
      {material && <><CatalogLabel code={material.code} name={material.name} /><p className="muted">Xem vật tư đã xuất hiện trong BOM, phiếu tính, lần trừ tồn và điều chuyển kho.</p></>}
      {history.isLoading ? <div className="loading-space">Đang tải lịch sử sử dụng…</div> : !history.data?.items.length ? <EmptyState title="Chưa có lịch sử sử dụng" detail="Vật tư này chưa nằm trong BOM, phiếu tính hoặc chứng từ kho nào." /> : <>
        <div className="table-scroll"><table className="material-history-table"><thead><tr><th>Thời điểm</th><th>Đã dùng cho</th><th>Loại</th><th>Số lượng</th><th>Trạng thái</th></tr></thead><tbody>{history.data.items.map((item) => <tr key={item.id}>
          <td>{formatDate(item.occurredAt)}</td>
          <td><strong>{item.title}</strong><small>{item.detail}</small></td>
          <td>{historyKindLabels[item.kind]}</td>
          <td className="num-cell">{formatNumber(item.quantity)}{item.unitName ? ` ${item.unitName}` : ""}</td>
          <td><span className="status active">{historyStatus(item.status)}</span></td>
        </tr>)}</tbody></table></div>
        <Pagination meta={history.data.meta} onPage={setPage} />
      </>}
      <div className="modal-actions"><Button type="button" variant="outline" onClick={onClose}>Đóng</Button></div>
    </div>
  </Modal>;
}

async function loadAllCatalogs(type: CatalogType): Promise<Catalog[]> {
  const first = await api<CatalogResponse>(`/catalogs/${type}?page=1&pageSize=100`);
  if (first.meta.totalPages <= 1) return first.items;
  const remaining = await Promise.all(
    Array.from({ length: first.meta.totalPages - 1 }, (_, index) =>
      api<CatalogResponse>(`/catalogs/${type}?page=${index + 2}&pageSize=100`),
    ),
  );
  return [...first.items, ...remaining.flatMap((response) => response.items)];
}

function CatalogImportModal({
  type,
  open,
  onClose,
}: {
  type: CatalogType;
  open: boolean;
  onClose(): void;
}) {
  const queryClient = useQueryClient();
  const [check, setCheck] = useState<ImportCheck | null>(emptyImportCheck);
  const [reading, setReading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) {
      setCheck(null);
      setError("");
      setReading(false);
      setSaving(false);
    }
  }, [open, type]);

  async function chooseFile(file?: File) {
    if (!file) return;
    setReading(true);
    setError("");
    setCheck(null);
    try {
      const [XLSX, catalogs, units] = await Promise.all([
        import("xlsx"),
        loadAllCatalogs(type),
        type === "material" ? loadAllCatalogs("unit") : Promise.resolve([] as Catalog[]),
      ]);
      const workbook = XLSX.read(await file.arrayBuffer(), { type: "array" });
      const sheetName = workbook.SheetNames[0];
      if (!sheetName) throw new Error("Tệp Excel chưa có trang tính dữ liệu.");
      const sheet = workbook.Sheets[sheetName];
      if (!sheet) throw new Error("Không thể đọc trang tính dữ liệu.");
      const matrix = XLSX.utils.sheet_to_json<unknown[]>(sheet, {
        header: 1,
        defval: "",
        raw: false,
        blankrows: false,
      });
      setCheck(checkCatalogImport(file.name, sheetName, matrix, type, catalogs, units));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Không thể đọc tệp Excel.");
    } finally {
      setReading(false);
    }
  }

  const validRows = check?.rows.filter((row) => !row.errors.length) ?? [];
  const invalidRows = check?.rows.filter((row) => row.errors.length) ?? [];
  const updates = validRows.filter((row) => row.action === "update").length;

  async function saveImport() {
    if (!check || invalidRows.length || !validRows.length) return;
    setSaving(true);
    setError("");
    try {
      for (const row of validRows) {
        const attributes = type === "material" && row.includesSupplier
          ? attributesWithSupplier(row.existing?.attributes ?? {}, row.supplier)
          : (row.existing?.attributes ?? {});
        const body = JSON.stringify({
          code: row.code,
          name: row.name,
          attributes,
          ...(type === "material" ? { defaultUnitId: row.defaultUnitId ?? null } : {}),
          isActive: row.existing?.isActive ?? true,
        });
        if (row.action === "update" && row.existing) {
          await api(`/catalogs/${type}/${row.existing.id}`, { method: "PATCH", body });
        } else {
          await api(`/catalogs/${type}`, { method: "POST", body });
        }
      }
      await queryClient.invalidateQueries({ queryKey: ["catalogs"] });
      onClose();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Không thể lưu dữ liệu từ tệp Excel.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      title={`Nhập ${catalogLabel(type).toLocaleLowerCase("vi")} từ Excel`}
      onClose={saving ? () => undefined : onClose}
    >
      <div className="modal-form">
        {!check && (
          <label className="drop-zone">
            <FileSpreadsheet size={38} />
            <strong>{reading ? "Đang kiểm tra tệp Excel…" : "Chọn tệp Excel để nhập"}</strong>
            <span>{type === "material" ? "Tệp vật tư cần có Mã, Tên, Mã ĐVT; cột Nhà cung cấp có thể để trống. Mã ĐVT phải có sẵn trong danh mục Đơn vị tính." : "Tệp cần có hai cột: Mã và Tên. Dữ liệu được kiểm tra trước khi lưu."}</span>
            <input
              type="file"
              accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              disabled={reading}
              onChange={(event) => chooseFile(event.target.files?.[0])}
            />
          </label>
        )}
        {error && <p className="form-error">{error}</p>}
        {check && (
          <>
            <p>
              <strong>{check.fileName}</strong> · Trang tính {check.sheetName}
            </p>
            <div className="summary-cards">
              <div>
                <span>Hợp lệ</span>
                <strong>{validRows.length}</strong>
              </div>
              <div className="valid">
                <span>Sẽ cập nhật</span>
                <strong>{updates}</strong>
              </div>
              <div className="warning">
                <span>Cần sửa</span>
                <strong>{invalidRows.length + check.messages.length}</strong>
              </div>
            </div>
            {check.messages.map((message) => (
              <p className="form-error" key={message}>{message}</p>
            ))}
            <div className="issues">
              <table>
                <thead>
                  <tr>
                    <th>Dòng</th>
                    <th>Mã</th>
                    <th>Tên</th>
                    {type === "material" && <><th>Mã ĐVT</th><th>Nhà cung cấp</th></>}
                    <th>Kết quả kiểm tra</th>
                  </tr>
                </thead>
                <tbody>
                  {check.rows.slice(0, 50).map((row) => (
                    <tr key={row.rowNo}>
                      <td>{row.rowNo}</td>
                      <td className="code-cell">{row.code || "—"}</td>
                      <td>{row.name || "—"}</td>
                      {type === "material" && <><td>{row.unitCode || "—"}</td><td>{row.supplier || "—"}</td></>}
                      <td>
                        {row.errors.length ? (
                          <span className="issue-error">{row.errors.join(" ")}</span>
                        ) : row.action === "update" ? (
                          <span className="issue-warning">Trùng mã hiện có — sẽ cập nhật</span>
                        ) : (
                          <span className="status active">Sẵn sàng thêm</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {check.rows.length > 50 && <p className="muted">Chỉ hiển thị 50 dòng đầu tiên.</p>}
          </>
        )}
        <div className="modal-actions">
          <Button type="button" variant="outline" disabled={saving} onClick={onClose}>
            Hủy
          </Button>
          {check && (
            <Button type="button" disabled={saving || Boolean(invalidRows.length) || Boolean(check.messages.length) || !validRows.length} onClick={saveImport}>
              <Upload size={16} /> Lưu {validRows.length} dòng hợp lệ
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
}

function checkCatalogImport(
  fileName: string,
  sheetName: string,
  matrix: unknown[][],
  type: CatalogType,
  catalogs: Catalog[],
  units: Catalog[],
): ImportCheck {
  const headers = matrix[0] ?? [];
  const codeColumn = headers.findIndex((header) => {
    const normalized = normalizedHeader(header);
    return normalized === "ma" || normalized === "code";
  });
  const nameColumn = headers.findIndex((header) => {
    const normalized = normalizedHeader(header);
    return normalized === "ten" || normalized === "name";
  });
  const supplierColumn = headers.findIndex((header) => {
    const normalized = normalizedHeader(header);
    return normalized === "nhacungcap" || normalized === "supplier" || normalized === "vendor";
  });
  const unitColumn = headers.findIndex((header) => {
    const normalized = normalizedHeader(header);
    return normalized === "madvt" || normalized === "madonvitinh" || normalized === "unitcode" || normalized === "uomcode";
  });
  const messages: string[] = [];
  if (codeColumn < 0) messages.push("Không tìm thấy cột Mã trong tệp Excel.");
  if (nameColumn < 0) messages.push("Không tìm thấy cột Tên trong tệp Excel.");
  if (type === "material" && unitColumn < 0) messages.push("Tệp vật tư cần có cột Mã ĐVT.");
  const existingByCode = new Map(catalogs.map((item) => [codeKey(item.code), item]));
  const unitByCode = new Map(units.filter((unit) => unit.isActive).map((unit) => [codeKey(unit.code), unit]));
  const rows = matrix.slice(1).map((source, index) => {
    const code = codeColumn < 0 ? "" : normalizedText(source[codeColumn]);
    const name = nameColumn < 0 ? "" : normalizedText(source[nameColumn]);
    const unitCode = unitColumn < 0 ? "" : normalizedText(source[unitColumn]);
    const supplier = supplierColumn < 0 ? "" : normalizedText(source[supplierColumn]);
    const existing = existingByCode.get(codeKey(code));
    const errors: string[] = [];
    if (!code) errors.push("Thiếu mã.");
    else if (code.length > 80) errors.push("Mã vượt quá 80 ký tự.");
    if (!name) errors.push("Thiếu tên.");
    else if (name.length > 240) errors.push("Tên vượt quá 240 ký tự.");
    const unit = unitByCode.get(codeKey(unitCode));
    if (type === "material") {
      if (!unitCode) errors.push("Thiếu mã ĐVT.");
      else if (!unit) errors.push("Mã ĐVT chưa có hoặc đã ngừng dùng.");
    }
    if (supplier.length > 240) errors.push("Nhà cung cấp vượt quá 240 ký tự.");
    return {
      rowNo: index + 2,
      code,
      name,
      unitCode,
      defaultUnitId: unit?.id,
      supplier,
      includesSupplier: supplierColumn >= 0,
      action: existing ? "update" : "create",
      existing,
      errors,
    } satisfies CatalogImportRow;
  });
  const rowCountByCode = new Map<string, number>();
  rows.forEach((row) => {
    if (row.code) {
      const key = codeKey(row.code);
      rowCountByCode.set(key, (rowCountByCode.get(key) ?? 0) + 1);
    }
  });
  rows.forEach((row) => {
    if (row.code && (rowCountByCode.get(codeKey(row.code)) ?? 0) > 1)
      row.errors.push("Mã bị lặp trong tệp.");
  });
  if (!rows.length && !messages.length) messages.push("Tệp Excel chưa có dòng dữ liệu để nhập.");
  return { fileName, sheetName, rows, messages };
}

function CatalogModal({
  type,
  item,
  onClose,
}: {
  type: CatalogType;
  item: Catalog | null | "new";
  onClose(): void;
}) {
  const queryClient = useQueryClient();
  const units = useQuery({
    queryKey: ["catalog-default-units"],
    queryFn: () => api<CatalogResponse>("/catalogs/unit?page=1&pageSize=100&active=true"),
    enabled: type === "material" && Boolean(item),
  });
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [defaultUnitId, setDefaultUnitId] = useState("");
  const [supplier, setSupplier] = useState("");
  const [active, setActive] = useState(true);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (item && item !== "new") {
      setCode(item.code);
      setName(item.name);
      setDefaultUnitId(item.defaultUnitId ?? "");
      setSupplier(materialSupplier(item));
      setActive(item.isActive);
    } else {
      setCode("");
      setName("");
      setDefaultUnitId("");
      setSupplier("");
      setActive(true);
    }
    setError("");
  }, [item]);
  async function save(event: React.FormEvent) {
    event.preventDefault();
    if (!item) return;
    setSaving(true);
    setError("");
    try {
      const body = {
        code,
        name,
        ...(type === "material" ? { defaultUnitId: defaultUnitId || null } : {}),
        attributes: type === "material" ? attributesWithSupplier(item === "new" ? {} : item.attributes, supplier) : (item === "new" ? {} : item.attributes),
        isActive: active,
      };
      await api(item === "new" ? `/catalogs/${type}` : `/catalogs/${type}/${item.id}`, {
        method: item === "new" ? "POST" : "PATCH",
        body: JSON.stringify(body),
      });
      await queryClient.invalidateQueries({ queryKey: ["catalogs"] });
      onClose();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Không thể lưu.");
    } finally {
      setSaving(false);
    }
  }
  return (
    <Modal
      open={Boolean(item)}
      title={item === "new" ? `Thêm ${catalogLabel(type).toLocaleLowerCase("vi")}` : `Cập nhật ${catalogLabel(type).toLocaleLowerCase("vi")}`}
      onClose={onClose}
    >
      <form className="modal-form" onSubmit={save}>
        <FormField label="Mã">
          <input required maxLength={80} value={code} onChange={(event) => setCode(event.target.value)} />
        </FormField>
        <FormField label="Tên">
          <input required maxLength={240} value={name} onChange={(event) => setName(event.target.value)} />
        </FormField>
        {type === "material" && <FormField label="Đơn vị tính chuẩn">
          <select required value={defaultUnitId} disabled={units.isLoading} onChange={(event) => setDefaultUnitId(event.target.value)}>
            <option value="">{units.isLoading ? "Đang tải đơn vị tính…" : "Chọn đơn vị tính"}</option>
            {(units.data?.items ?? []).map((unit) => <option key={unit.id} value={unit.id}>{unit.code} — {unit.name}</option>)}
          </select>
          <small className="form-help">Đơn vị tính được chọn tại đây sẽ dùng thống nhất cho vật tư này.</small>
        </FormField>}
        {type === "material" && <FormField label="Nhà cung cấp">
          <input maxLength={240} value={supplier} onChange={(event) => setSupplier(event.target.value)} placeholder="Ví dụ: Công ty ABC" />
        </FormField>}
        <FormField label="Trạng thái">
          <select value={active ? "true" : "false"} onChange={(event) => setActive(event.target.value === "true")}>
            <option value="true">Đang dùng</option>
            <option value="false">Ngừng dùng</option>
          </select>
        </FormField>
        {error && <p className="form-error">{error}</p>}
        <div className="modal-actions">
          <Button type="button" variant="outline" onClick={onClose}>Hủy</Button>
          <Button type="submit" disabled={saving || (type === "material" && (units.isLoading || !defaultUnitId))}>Lưu</Button>
        </div>
      </form>
    </Modal>
  );
}

function DeleteButton({ type, id, name }: { type: CatalogType; id: string; name: string }) {
  const client = useQueryClient();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  async function remove() {
    setBusy(true);
    try {
      await api(`/catalogs/${type}/${id}`, { method: "DELETE" });
      await client.invalidateQueries({ queryKey: ["catalogs"] });
      setOpen(false);
    } finally {
      setBusy(false);
    }
  }
  return (
    <>
      <button onClick={() => setOpen(true)} title="Xóa" aria-label={`Xóa ${name}`}>
        <Trash2 size={17} />
      </button>
      <ConfirmDialog
        open={open}
        title="Xóa danh mục"
        message={`Bạn có chắc muốn xóa “${name}”? Thao tác này không thể hoàn tác.`}
        confirmLabel="Xóa danh mục"
        pending={busy}
        onClose={() => setOpen(false)}
        onConfirm={remove}
      />
    </>
  );
}

export function formatDate(value?: string | null) {
  if (!value) return "—";
  const date = new Date(
    /^\d{4}-\d{2}-\d{2}$/.test(value)
      ? `${value}T00:00:00+07:00`
      : /(?:Z|[+-]\d{2}:?\d{2})$/i.test(value)
        ? value
        : `${value.replace(" ", "T")}Z`,
  );
  return Number.isNaN(date.valueOf())
    ? value
    : new Intl.DateTimeFormat("vi-VN", {
        timeZone: "Asia/Ho_Chi_Minh",
        dateStyle: "short",
        timeStyle: "short",
      }).format(date);
}
